import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import io from "socket.io-client";
import {
  cacheTrips,
  getTripId,
  normalizeTrip,
  normalizeTrips,
  readCachedTrips,
  VIEW_MODES,
} from "../utils/tripUtils";

const SOCKET_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export const useSocket = ({
  mode = VIEW_MODES.ALL_TRIPS,
  selectedTripId = null,
} = {}) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState("connecting");
  const [activeTrips, setActiveTrips] = useState(() => readCachedTrips());
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [error, setError] = useState(null);

  const upsertTrip = useCallback((incomingTrip) => {
    const normalized = normalizeTrip(incomingTrip);
    if (!normalized) return;

    setActiveTrips((prev) => {
      const byId = {};
      normalizeTrips(prev).forEach((trip) => {
        byId[getTripId(trip)] = trip;
      });
      byId[getTripId(normalized)] = {
        ...byId[getTripId(normalized)],
        ...normalized,
      };
      const next = Object.values(byId);
      cacheTrips(next);
      return next;
    });
    setLastSyncedAt(Date.now());
  }, []);

  const removeTrip = useCallback((tripId) => {
    setActiveTrips((prev) => {
      const next = normalizeTrips(prev).filter((trip) => getTripId(trip) !== tripId);
      cacheTrips(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 8000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setIsConnected(true);
      setConnectionState("connected");
      setError(null);
      socket.emit("globalTripsData");
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
      setConnectionState("disconnected");
    });

    socket.on("reconnect_attempt", () => {
      setConnectionState("reconnecting");
    });

    socket.on("connect_error", (socketError) => {
      setIsConnected(false);
      setConnectionState("error");
      setError(socketError?.message || "Socket connection failed");
    });

    socket.on("updateGlobalTripsData", (tripsData) => {
      const nextTrips = normalizeTrips(tripsData);
      setActiveTrips(nextTrips);
      cacheTrips(nextTrips);
      setLastSyncedAt(Date.now());
    });

    socket.on("broadcastDriverData", (data) => {
      upsertTrip(data);
    });

    socket.on("tripEnded", ({ tripId }) => {
      removeTrip(tripId);
    });

    socket.on("trackingConfirmed", ({ tripId }) => {
      if (tripId) setConnectionState("tracking");
    });

    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.off("reconnect_attempt");
      socket.off("connect_error");
      socket.off("updateGlobalTripsData");
      socket.off("broadcastDriverData");
      socket.off("tripEnded");
      socket.off("trackingConfirmed");
      socket.disconnect();
    };
  }, [removeTrip, upsertTrip]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !isConnected) return;

    if (
      (mode === VIEW_MODES.SELECTED_TRIP || mode === VIEW_MODES.DEDICATED_TRACKING) &&
      selectedTripId
    ) {
      socket.emit("trackDriver", { tripId: selectedTripId });
      return;
    }

    socket.emit("globalTripsData");
  }, [isConnected, mode, selectedTripId]);

  const requestAllTrips = useCallback(() => {
    socketRef.current?.emit("globalTripsData");
  }, []);

  const trackTrip = useCallback((tripId) => {
    if (!tripId) return;
    socketRef.current?.emit("trackDriver", { tripId });
  }, []);

  return useMemo(
    () => ({
      socket: socketRef.current,
      socketUrl: SOCKET_URL,
      isConnected,
      connectionState,
      activeTrips,
      lastSyncedAt,
      error,
      requestAllTrips,
      trackTrip,
    }),
    [
      activeTrips,
      connectionState,
      error,
      isConnected,
      lastSyncedAt,
      requestAllTrips,
      trackTrip,
    ],
  );
};
